/**
 * PageConfigPanel Component for UI-Model-1
 *
 * Configuration panel for editing page properties.
 * Shows when a page is selected in the NavigationPreview.
 */

import React, { useCallback } from 'react';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import TextField from 'Components/DesignSystem/TextField/TextField';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useSaveQueue } from '../../contexts/SaveQueueContext';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';
import { createUpdatePageCommand } from '../../types/command.types';
import { PageAPI } from '../../types/api.types';
import { PageContainersList } from './PageContainersList';

interface PageConfigPanelProps {
  id?: string;
  pageId: string;
  groupId: string;
}

export const PageConfigPanel = ({ id, pageId, groupId }: PageConfigPanelProps) => {
  const baseId = id || 'page-config-panel';
  const { state, dispatch } = useUIModelState();
  const { enqueueSave } = useSaveQueue();
  const navigation = state.model?.layout.navigation;

  // Find the group and page
  const groups = state.model?.layout.navigation.groups || [];
  const group = groups.find((g) => g.id === groupId);
  const page = group?.pages.find((p) => p.id === pageId);

  // Helper to dispatch page updates
  const updatePage = useCallback(
    (updates: Partial<Pick<PageAPI, 'name' | 'configuration'>>, description?: string) => {
      const command = createUpdatePageCommand(
        pageId,
        groupId,
        updates,
        description || 'Update page configuration'
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: command });
      dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

      if (navigation) {
        const updatedGroups = navigation.groups.map((g) => {
          if (g.id !== groupId) return g;
          const updatedPages = g.pages.map((p) => {
            if (p.id !== pageId) return p;
            let updatedPage = {
              ...p,
              ...updates,
              configuration: updates.configuration
                ? { ...p.configuration, ...updates.configuration }
                : p.configuration,
            };
            if (updates.name && updatedPage.configuration) {
              updatedPage = {
                ...updatedPage,
                configuration: { ...updatedPage.configuration, name: updates.name },
              };
            }
            return updatedPage;
          });
          return { ...g, pages: updatedPages };
        });
        enqueueSave('navigation', { navigationData: { ...navigation, groups: updatedGroups } });
      }
    },
    [dispatch, pageId, groupId, navigation, enqueueSave]
  );

  // Debounced input for page title
  const pageTitleInput = useDebouncedInput(
    page?.name || '',
    (value) => updatePage({ name: value }, 'Update page title'),
    500
  );

  if (!group || !page) {
    return (
      <div id={baseId} data-testid={baseId} className="tw-p-4 tw-text-ink-secondary tw-text-sm">
        Page not found
      </div>
    );
  }

  const autoAdvance = page.configuration?.autoAdvance || false;

  return (
    <div id={baseId} data-testid={baseId} className="tw-space-y-6">
      {/* Section: Page Settings */}
      <div className="tw-space-y-4">
        <FieldTitle label="Page Settings" size="sm" />

        <TextField
          label="Page Title"
          labelSize="sm"
          value={pageTitleInput.localValue}
          onChange={pageTitleInput.handleChange}
          placeholder="Enter page title"
        />

        <div className="tw-space-y-1">
          <Toggle
            label="Auto Advance"
            checked={autoAdvance}
            onChange={(checked) =>
              updatePage(
                { configuration: { ...page.configuration, autoAdvance: checked } },
                `${checked ? 'Enable' : 'Disable'} auto advance`
              )
            }
          />
          <p className="tw-text-xs tw-text-ink-secondary tw-pl-10">
            Automatically advance to the next page when all fields are complete
          </p>
        </div>
      </div>

      {/* Section: Containers — drag-and-drop reorder */}
      {page.canvasId && <PageContainersList canvasId={page.canvasId} />}
    </div>
  );
};

export default PageConfigPanel;
